import React, { useState } from 'react';
import { X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useTaskStore } from '../../stores/taskStore';
import { useHabitStore } from '../../stores/habitStore';
import { useRoutineStore } from '../../stores/routineStore';
import { useProjectStore } from '../../stores/projectStore';
import { useAbilitiesStore } from '../../stores/abilitiesStore';
import { useQuestStore } from '../../stores/questStore';
import { useScheduleStore } from '../../stores/scheduleStore';
import { reshapeLevels, getTreeShape } from '../../utils/treeShape';
import { ensureCapacityForBatch, type CapacityRequest } from '../../utils/capacityGuard';
import { applyChargeFor } from '../../constants/applyPrices';
import { resolveCategoryId } from '../../constants/categories';
import { spendCoin } from '../../services/aiProposalApply';
import {
  applyTaskFolder, createTaskWithSubtasks, folderName, folderTasks, subtaskTitles,
} from '../../services/applyTaskFolder';
import { useWalletStore } from '../../stores/walletStore';
import { CoinAmount } from '../CoinAmount';
import { pickMissingDays } from '../../utils/habitDaysPrompt';
import type { HabitInterval, HabitSchedule } from '../../types';

interface SuggestionPreviewProps {
  suggestion: any;
  onClose: () => void;
  onApply: () => void;
}

// The AI is asked to emit one of these as the item `category`; coerce anything
// else (a capitalized name, a stray label) to a valid domain id so the item
// lands in a real domain rather than an empty bucket.
const DOMAIN_IDS = ['personal', 'social', 'occupational', 'intellectual', 'avocational', 'ecological', 'existential'];

/**
 * ⚠️⚠️ THE FALLBACK IS `personal`, WHICH MAKES EVERY MISS A SILENT MISFILE — not
 * an error, not an empty bucket, just an item quietly in the wrong domain.
 *
 * ⭐ That is why a DISPLAY NAME must be resolved before falling back. The
 * Material domain kept the id `ecological` when it was renamed, so a model that
 * writes the name it sees on screen — `category: 'material'` — was landing every
 * one of those items in Personal. `resolveCategoryId` knows every name a domain
 * has ever had, so it catches both the current display name and the old one.
 */
export function normalizeCategory(c?: string): string {
  if (!c) return 'personal';
  const lc = String(c).toLowerCase().trim();
  if (DOMAIN_IDS.includes(lc)) return lc;
  const byName = resolveCategoryId(lc);
  return byName && DOMAIN_IDS.includes(byName) ? byName : 'personal';
}

const HABIT_INTERVALS = ['daily', 'weekly', 'monthly', 'yearly'];
function normalizeInterval(i?: string): string {
  const lc = String(i ?? 'daily').toLowerCase().trim();
  return HABIT_INTERVALS.includes(lc) ? lc : 'daily';
}

const REQ_TYPES = ['read', 'watch', 'listen', 'practice', 'create', 'complete'];
function normalizeReqType(type: any): string {
  return REQ_TYPES.includes(String(type)) ? String(type) : 'complete';
}

/** The checklist steps a task will be created with — shown so nothing lands unseen. */
function SubtaskList({ task }: { task: unknown }) {
  const steps = subtaskTitles(task as { subtasks?: unknown });
  if (steps.length === 0) return null;
  return (
    <ul className="mt-1.5 space-y-0.5 border-l border-border pl-3">
      {steps.map((s, i) => (
        <li key={i} className="text-xs text-muted-foreground">{s}</li>
      ))}
    </ul>
  );
}

export function SuggestionPreview({ suggestion, onClose, onApply }: SuggestionPreviewProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isApplying, setIsApplying] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);

  /**
   * ⭐⭐ WHAT THIS APPLY COSTS, READ LIVE. `walletStore.aiPrices` holds ONLY
   * rows with `active = true`, so `priceFor` is `null` for a price the owner
   * has not switched on — and the whole charge is skipped. That is what makes
   * migration 0246 safe to run before the owner is ready: the rows land
   * inactive and nothing here changes until they say so.
   */
  const charge = applyChargeFor(suggestion);
  const unitCp = useWalletStore((s) => (charge ? s.priceFor(charge.key) : null));
  const totalCp = charge && unitCp ? unitCp * charge.qty : 0;

  // This applier builds best-effort objects from untyped AI suggestion data and
  // calls each store's REAL mutator (signatures verified in AI2 hardening). The
  // mutators are loosely aliased because the source data is untyped.
  const addTask = useTaskStore((s) => s.addTask) as (...a: any[]) => any;
  const addHabit = useHabitStore((s) => s.addHabit) as (...a: any[]) => string;
  const addRoutine = useRoutineStore((s) => s.addRoutine) as (...a: any[]) => any;
  const addProject = useProjectStore((s) => s.addProject) as (...a: any[]) => string;
  const addPhase = useProjectStore((s) => s.addPhase) as (...a: any[]) => any;
  const deletePhase = useProjectStore((s) => s.deletePhase) as (...a: any[]) => any;
  // ⚠️ The PROJECT's own task mutator — `(projectId, task)` — not taskStore's.
  const addProjectTask = useProjectStore((s) => s.addTask) as (...a: any[]) => any;
  const projectState = useProjectStore.getState as () => any;
  const addAbility = useAbilitiesStore((s) => s.addAbility) as (...a: any[]) => string;
  const addSkill = useAbilitiesStore((s) => s.addSkill) as (...a: any[]) => string;
  const updateSkill = useAbilitiesStore((s) => s.updateSkill) as (...a: any[]) => any;
  const addRequirement = useAbilitiesStore((s) => s.addRequirement) as (...a: any[]) => any;
  const addQuest = useQuestStore((s) => s.addQuest) as (...a: any[]) => string;
  const addGoal = useQuestStore((s) => s.addGoal) as (...a: any[]) => any;
  const handleSaveTemplate = useScheduleStore((s) => s.handleSaveTemplate);

  const toggleExpanded = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApply = async () => {
    setIsApplying(true);
    setChargeError(null);

    /**
     * ⚠️ Goes through `createTaskWithSubtasks` so a task's "subtasks" become real
     * CHILD tasks (owner, 2026-09-17) instead of being silently dropped.
     */
    const applyTask = (t: any, extra: Record<string, any> = {}) => {
      if (Object.keys(extra).length === 0) {
        createTaskWithSubtasks(t, { category: normalizeCategory(t.category) });
        return;
      }
      addTask({
        id: crypto.randomUUID(),
        title: t.title,
        description: t.description || '',
        category: normalizeCategory(t.category),
        priority: t.priority || 'medium',
        duration: t.duration || 30,
        completed: false,
        ...extra,
      });
    };

    // The days the person picked below, before the charge (weekly / monthly / yearly).
    const scheduleByHabit = new Map<any, HabitSchedule>();
    const applyHabit = (h: any): string =>
      addHabit({
        name: h.name,
        description: h.description || '',
        category: normalizeCategory(h.category),
        icon: h.icon || 'Circle',
        schedule: scheduleByHabit.get(h) ?? { interval: normalizeInterval(h.interval) as HabitInterval },
      });

    // A routine is a sequence of habits — materialise each suggested step as a
    // real habit, then reference it from a 'habit' step (the model's shape).
    const applyRoutine = (r: any) => {
      const category = normalizeCategory(r.category);
      const rawSteps = r.habits || r.steps || [];
      const steps = rawSteps.map((s: any, i: number) => {
        const habitId = addHabit({
          name: s.name,
          description: '',
          category,
          icon: 'Circle',
          schedule: { interval: 'daily' },
        });
        return {
          id: crypto.randomUUID(),
          type: 'habit',
          habitId,
          duration: s.duration || 5,
          order: s.order !== undefined ? s.order : i,
        };
      });
      addRoutine({ name: r.name, description: r.description || '', category, steps });
    };

    /**
     * ⚠️⚠️ A PROJECT'S TASKS LIVE ON THE PROJECT, NOT IN `taskStore`.
     *
     * This used to call `applyTask({...}, { projectId })`, which writes a row to
     * the GLOBAL task store. The project workspace renders `project.tasks`
     * grouped by `phaseId`, so every one of those tasks was created, carried the
     * right `projectId`, and was invisible: the project showed "No tasks yet" in
     * each phase and 0 in Task Breakdown while twelve tasks sat in the domain's
     * task list. Nothing errored — they went to a real place, just not this one.
     *
     * ⚠️⚠️ AND THE MODEL'S PHASES WERE STRUCTURALLY UNREACHABLE. `addProject`'s
     * signature OMITS `phases`, so it always seeds DEFAULT_PHASES (Initiation /
     * Research / Prototype / Test / Delivery). The four phases in the preview
     * could never have been created by passing them in — they have to be added
     * afterwards with `addPhase`.
     */
    const applyProject = (p: any) => {
      const category = normalizeCategory(p.category);
      const projectId = addProject({
        title: p.name || p.title,
        description: p.description || '',
        category,
        status: 'active',
        startDate: new Date().toISOString(),
        priority: p.priority || 'medium',
        complexity: p.complexity || 'medium',
        tags: Array.isArray(p.tags) ? p.tags.filter((tag: any) => tag && typeof tag === 'string') : [],
      });

      const phases = (Array.isArray(p.phases) ? p.phases : []).filter(Boolean);
      // No phases from the model — keep the default scaffold and place nothing.
      if (phases.length === 0) return;

      // ⚠️ `addPhase` returns void, so the new ids have to be read back. Snapshot
      // the seeded ids FIRST and diff — matching on title would collide whenever
      // the model names a phase "Research", which is also a default.
      const phaseIdsBefore = new Set(
        (projectState().projects.find((x: any) => x.id === projectId)?.phases ?? [])
          .map((ph: any) => ph.id),
      );

      phases.forEach((phase: any) => {
        addPhase(projectId, { title: phase.name || phase.title || 'Phase', collapsed: false });
      });

      // ⚠️⚠️ READ THE STORE AT CALL TIME, NOT FROM A RENDER CLOSURE. Every write
      // above happened in this same handler, so the component's `projects` prop
      // is a snapshot from before any of them — `getState()` is the only view
      // that includes the phases just added. [[stale-read-async-store]]
      const project = projectState().projects.find((x: any) => x.id === projectId);
      if (!project) return;
      const added = project.phases.filter((ph: any) => !phaseIdsBefore.has(ph.id));

      added.forEach((ph: any, i: number) => {
        const source = phases[i];
        for (const task of source?.tasks ?? []) {
          // Tasks may arrive as bare strings or as objects.
          const title = typeof task === 'string' ? task : task?.title;
          if (!title) continue;
          addProjectTask(projectId, {
            title,
            description: typeof task === 'object' ? task.description || '' : '',
            completed: false,
            status: 'todo',
            priority: 'medium',
            phaseId: ph.id,
          });
        }
      });

      // The five default phases are now empty scaffolding beside the real plan.
      // ⚠️ Removed only when the model supplied its own — a project with no AI
      // phases still needs them.
      phaseIdsBefore.forEach((id) => deletePhase(projectId, id as string));
    };

    const applyAbility = (a: any) => {
      const category = normalizeCategory(a.category);
      const shape = getTreeShape(a.shape).id;
      const abilityId = addAbility({
        name: a.name,
        description: a.description || '',
        category,
        icon: a.icon || 'Zap',
        shape,
        skills: [],
      });
      // Apply the chosen shape + a logical dependency graph (local ids), then apply
      // in two passes: create every skill first (the store rejects deps on a
      // not-yet-created skill), then set dependsOn via updateSkill once all exist.
      const levels = reshapeLevels(a.skills || [], shape);
      const idMap = new Map<string, string>();
      const depPlan: Array<{ storeId: string; localDeps: string[] }> = [];
      for (const levelData of levels) {
        for (const skillData of levelData.skills) {
          const skillId = addSkill(abilityId, {
            name: skillData.name,
            description: skillData.description || '',
            level: levelData.level,
            isCapstone: skillData.isCapstone,
            requirements: [],
          });
          idMap.set(skillData.id, skillId);
          if (Array.isArray(skillData.dependsOn) && skillData.dependsOn.length > 0) {
            depPlan.push({ storeId: skillId, localDeps: skillData.dependsOn });
          }
          for (const req of skillData.requirements || []) {
            addRequirement(abilityId, skillId, {
              title: req.title,
              type: normalizeReqType(req.type),
              requirementType: 'input',
            });
          }
        }
      }
      for (const { storeId, localDeps } of depPlan) {
        const resolved = localDeps.map((d) => idMap.get(d)).filter((x: any): x is string => !!x);
        if (resolved.length === 0) continue;
        // Per-skill guard: a rejected dependency must not abort the remaining tiers.
        try {
          updateSkill(abilityId, storeId, { dependsOn: resolved });
        } catch (err) {
          console.warn('Skipped a skill dependency that failed validation:', err);
        }
      }
    };

    const applyQuest = (q: any) => {
      const folderId = useQuestStore.getState().folders[0]?.id ?? '';
      const questId = addQuest({
        title: q.title,
        description: q.description || '',
        category: normalizeCategory(q.category),
        folderId,
        status: 'active',
      });
      for (const g of q.goals || []) {
        addGoal(questId, {
          actionVerb: g.actionVerb || 'Complete',
          what: g.what || '',
          quantity: typeof g.quantity === 'number' ? g.quantity : 1,
          byWhen: typeof g.byWhen === 'string' ? g.byWhen : undefined,
        });
      }
    };

    const applyTemplate = (name: string, blocks: any[]) => {
      handleSaveTemplate({
        id: crypto.randomUUID(),
        name,
        blocks: (blocks || []).map((b: any) => ({
          id: crypto.randomUUID(),
          name: b.title || b.name || 'Block',
          startTime: b.startTime,
          endTime: b.endTime,
          category: normalizeCategory(b.category),
          type: 'time',
        })),
      } as any);
    };

    /**
     * ⚠️⚠️ CAPACITY IS CHECKED AT THE DISPATCH, NOT INSIDE THE `apply*` HELPERS.
     * A suggestion is a TREE — a project carries its tasks, an ability its
     * skills, a quest its goals, a routine its habits — and those children
     * belong to a parent that does not exist yet, so their per-parent holding is
     * 0 and a check on them could never fire. Guarding the root of each tree is
     * the whole gate; children ride with the parent they are part of, exactly as
     * they do in `NewAbilityModal`.
     *
     * ⚠️⚠️ **ALL-OR-NOTHING NOW THAT APPLYING CHARGES** (2026-08-14). This used
     * to check each item as it went and SKIP the blocked ones, so the rest of a
     * multi-item suggestion still landed. That is the wrong trade once Coin is
     * involved: the charge is computed from what the user pressed Apply on, so a
     * skipped item is one they paid for and never received.
     * `ensureCapacityForBatch` reads every request against the live stores and
     * commits to nothing — its own doc states it must run before any write AND
     * before any charge, for exactly this reason.
     */
    const req = (
      type: CapacityRequest['type'], category: any, title?: string,
    ): CapacityRequest => ({
      type, domainId: normalizeCategory(category), draftTitle: title,
    });

    const s = suggestion;
    const reqs: CapacityRequest[] = [];
    // ⚠️⚠️ A SUBTASK IS A REAL TASK IN THE SAME DOMAIN, so it fills the allowance
    // like a top-level one (`TaskInspector.addChild` says the same) — counting
    // only the parents would let a batch through that does not fit.
    const taskReqs = (t: any, category: any) => {
      reqs.push(req('tasks', category, t.title));
      subtaskTitles(t).forEach((title) => reqs.push(req('tasks', category, title)));
    };

    if (s.type === 'tasks') {
      (s.items || []).forEach((t: any) => taskReqs(t, t.category));
    } else if (s.type === 'taskFolder') {
      // ⚠️ The TASKS are what capacity counts — a folder is not one of the ten
      // counted action types.
      folderTasks(s).forEach((t) => taskReqs(t, s.category));
    } else if (s.type === 'habits') {
      (s.items || []).forEach((h: any) => reqs.push(req('habits', h.category, h.name)));
    } else if (s.type === 'routine') {
      reqs.push(req('routines', s.category, s.name));
    } else if (s.type === 'project') {
      reqs.push(req('projects', s.category, s.name || s.title));
    } else if (s.type === 'ability') {
      reqs.push(req('abilities', s.category, s.name));
    } else if (s.type === 'quest') {
      reqs.push(req('quests', s.category, s.title));
    } else if (s.type === 'program') {
      // ⚠️ A schedule template is not one of the ten counted action types, so
      // `s.templates` contributes no capacity request here (nor below).
      (s.tasks || []).forEach((t: any) => taskReqs(t, t.category));
      (s.habits || []).forEach((h: any) => reqs.push(req('habits', h.category, h.name)));
      (s.routines || []).forEach((r: any) => reqs.push(req('routines', r.category, r.name)));
      (s.projects || []).forEach((p: any) => reqs.push(req('projects', p.category, p.name || p.title)));
      (s.quests || []).forEach((q: any) => reqs.push(req('quests', q.category, q.title)));
    }

    // ⚠️ Before the charge. A refusal here opens the capacity modal and leaves
    // the preview standing, so pressing Apply again after making room works.
    if (!ensureCapacityForBatch(reqs)) {
      setIsApplying(false);
      return;
    }

    /* ⚠️⚠️ THE DAYS COME FIRST — BEFORE THE CHARGE (owner, 2026-10-04: "ask for the
       days first, then charge"). A weekly / monthly / yearly habit used to be created
       with NO day, so it was never due (utils/habitSchedule.ts). Each one opens the day
       picker; cancelling any of them stops the apply — nothing created, nothing charged. */
    const suggestedHabits: any[] = s.type === 'habits' ? (s.items || []) : s.type === 'program' ? (s.habits || []) : [];
    const picked = await pickMissingDays(
      suggestedHabits.map((h: any) => ({ name: h.name, schedule: { interval: normalizeInterval(h.interval) as HabitInterval } })),
    );
    if (!picked) {
      setChargeError('Cancelled — nothing was created and nothing was charged.');
      setIsApplying(false);
      return;
    }
    suggestedHabits.forEach((h, i) => scheduleByHabit.set(h, picked[i]));

    try {
      /**
       * ⚠️⚠️ **CHARGE BEFORE CREATE, AND ONLY WHEN A PRICE IS LIVE.** `unitCp`
       * comes from the wallet's active-prices list, so an inactive key never
       * reaches `spend_coin` at all — which is what lets migration 0246 land
       * ahead of the owner's decision to switch charging on.
       * ⚠️ ONE call, never one per item: a suggestion carries a single `type`,
       * so there is exactly one price key and no way to half-charge a batch.
       */
      if (charge && unitCp && unitCp > 0) {
        const spend = await spendCoin(charge.key, charge.qty);
        if (!spend.ok) {
          setChargeError(
            `Not enough Coin — this costs ${spend.priceCp} cp and you have ${spend.balanceCp} cp. `
            + 'Nothing was created. Top up in Status → Wallet.',
          );
          setIsApplying(false);
          return;
        }
      }

      if (s.type === 'tasks') {
        (s.items || []).forEach((t: any) => applyTask(t));
      } else if (s.type === 'taskFolder') {
        await applyTaskFolder(s);
      } else if (s.type === 'habits') {
        (s.items || []).forEach((h: any) => applyHabit(h));
      } else if (s.type === 'routine') {
        applyRoutine(s);
      } else if (s.type === 'project') {
        applyProject(s);
      } else if (s.type === 'ability') {
        applyAbility(s);
      } else if (s.type === 'quest') {
        applyQuest(s);
      } else if (s.type === 'template') {
        applyTemplate(s.name, s.blocks);
      } else if (s.type === 'program') {
        (s.templates || []).forEach((t: any) => applyTemplate(t.name, t.blocks));
        (s.tasks || []).forEach((t: any) => applyTask(t));
        (s.habits || []).forEach((h: any) => applyHabit(h));
        (s.routines || []).forEach((r: any) => applyRoutine(r));
        (s.projects || []).forEach((p: any) => applyProject(p));
        (s.quests || []).forEach((q: any) => applyQuest(q));
      }

      await new Promise((resolve) => setTimeout(resolve, 300));
      onApply();
    } catch (error) {
      console.error('Failed to apply suggestion:', error);
      alert('Failed to apply some items. Please try again.');
    } finally {
      setIsApplying(false);
    }
  };

  const renderContent = () => {
    if (suggestion.type === 'taskFolder') {
      const tasks = folderTasks(suggestion);
      return (
        <div className="space-y-2">
          <div className="rounded-lg bg-muted p-3">
            <div className="font-medium text-foreground">{folderName(suggestion) || 'Folder'}</div>
            {suggestion.description && (
              <p className="mt-1 text-sm text-muted-foreground">{suggestion.description}</p>
            )}
            <div className="mt-2 flex gap-2 text-xs">
              <span className="rounded bg-background px-2 py-1">{normalizeCategory(suggestion.category)}</span>
              <span className="rounded bg-background px-2 py-1">{tasks.length} task{tasks.length === 1 ? '' : 's'}</span>
            </div>
          </div>
          <ul className="space-y-1.5 pl-3">
            {tasks.map((t, i) => (
              <li key={i} className="rounded-lg bg-muted/60 px-3 py-2">
                <span className="text-sm text-foreground">{t.title}</span>
                {t.description && <p className="mt-0.5 text-xs text-muted-foreground">{t.description}</p>}
                <SubtaskList task={t} />
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (suggestion.type === 'tasks' && suggestion.items) {
      return (
        <div className="space-y-2">
          {suggestion.items.map((task: any, i: number) => (
            <div key={i} className="bg-muted rounded-lg p-3">
              <div className="font-medium text-foreground">{task.title}</div>
              {task.description && (
                <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
              )}
              <SubtaskList task={task} />
              <div className="flex gap-2 mt-2 text-xs">
                <span className="px-2 py-1 bg-background rounded">{task.category}</span>
                <span className="px-2 py-1 bg-background rounded">{task.priority}</span>
                {task.duration && <span className="px-2 py-1 bg-background rounded">{task.duration}m</span>}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (suggestion.type === 'habits' && suggestion.items) {
      return (
        <div className="space-y-2">
          {suggestion.items.map((habit: any, i: number) => (
            <div key={i} className="bg-muted rounded-lg p-3">
              <div className="font-medium text-foreground">{habit.name}</div>
              {habit.description && (
                <p className="text-sm text-muted-foreground mt-1">{habit.description}</p>
              )}
              <div className="flex gap-2 mt-2 text-xs">
                <span className="px-2 py-1 bg-background rounded">{habit.category}</span>
                <span className="px-2 py-1 bg-background rounded">{habit.interval}</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (suggestion.type === 'routine') {
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.name}</div>
            {suggestion.description && (
              <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>
            )}
          </div>
          {suggestion.habits && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Steps:</div>
              {suggestion.habits.map((habit: any, i: number) => (
                <div key={i} className="bg-muted rounded-lg p-2 text-sm">
                  {i + 1}. {habit.name} - {habit.duration}m
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (suggestion.type === 'quest') {
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.title}</div>
            {suggestion.description && <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>}
            {suggestion.category && <span className="inline-block mt-2 text-xs px-2 py-1 bg-background rounded capitalize">{suggestion.category}</span>}
          </div>
          {Array.isArray(suggestion.goals) && suggestion.goals.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Goals</div>
              {suggestion.goals.map((g: any, i: number) => (
                <div key={i} className="bg-muted rounded-lg p-2 text-sm flex items-center justify-between gap-2">
                  <span className="text-foreground">{g.actionVerb} {g.what}{g.quantity > 1 ? ` ×${g.quantity}` : ''}</span>
                  {g.byWhen && <span className="text-xs text-muted-foreground shrink-0">by {g.byWhen}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (suggestion.type === 'project') {
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.name || suggestion.title}</div>
            {suggestion.description && <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>}
            {suggestion.category && <span className="inline-block mt-2 text-xs px-2 py-1 bg-background rounded capitalize">{suggestion.category}</span>}
          </div>
          {Array.isArray(suggestion.phases) && suggestion.phases.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Phases</div>
              {suggestion.phases.map((p: any, i: number) => (
                <div key={i} className="bg-muted rounded-lg p-2 text-sm">
                  <div className="font-medium text-foreground">{p.name}</div>
                  {Array.isArray(p.tasks) && p.tasks.length > 0 && (
                    <ul className="mt-1 ml-4 list-disc text-muted-foreground">
                      {p.tasks.map((t: any, j: number) => <li key={j}>{typeof t === 'string' ? t : t.title}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (suggestion.type === 'ability') {
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.name}</div>
            {suggestion.description && <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>}
            {suggestion.category && <span className="inline-block mt-2 text-xs px-2 py-1 bg-background rounded capitalize">{suggestion.category}</span>}
          </div>
          {Array.isArray(suggestion.skills) && suggestion.skills.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Skills</div>
              {suggestion.skills.map((lvl: any, i: number) => (
                <div key={i} className="bg-muted rounded-lg p-2 text-sm">
                  <div className="text-xs text-muted-foreground mb-1">Level {lvl.level}</div>
                  <ul className="ml-4 list-disc text-foreground">
                    {(lvl.skills || []).map((s: any, j: number) => <li key={j}>{s.name}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (suggestion.type === 'template') {
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.name}</div>
          </div>
          {Array.isArray(suggestion.blocks) && suggestion.blocks.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Time blocks</div>
              {suggestion.blocks.map((b: any, i: number) => (
                <div key={i} className="bg-muted rounded-lg p-2 text-sm flex items-center justify-between gap-2">
                  <span className="text-foreground">{b.title}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{b.startTime}–{b.endTime}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (suggestion.type === 'program') {
      const sections: Array<[string, any[]]> = [
        ['Templates', suggestion.templates], ['Tasks', suggestion.tasks], ['Habits', suggestion.habits],
        ['Routines', suggestion.routines], ['Projects', suggestion.projects], ['Quests', suggestion.quests],
      ];
      return (
        <div className="space-y-3">
          <div className="bg-muted rounded-lg p-3">
            <div className="font-medium text-foreground">{suggestion.name}</div>
            {suggestion.description && <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>}
          </div>
          <div className="space-y-2">
            {sections.filter(([, arr]) => Array.isArray(arr) && arr.length > 0).map(([label, arr]) => (
              <div key={label} className="bg-muted rounded-lg p-2 text-sm">
                <span className="font-medium text-foreground">{label}</span>
                <span className="text-muted-foreground"> · {arr.length}</span>
                <div className="mt-1 text-xs text-muted-foreground">
                  {arr.map((it: any) => it.name || it.title || it.actionVerb || '').filter(Boolean).join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Unknown shape — last-resort readable fallback.
    return (
      <div className="bg-muted rounded-lg p-3">
        <pre className="text-sm text-foreground whitespace-pre-wrap">
          {JSON.stringify(suggestion, null, 2)}
        </pre>
      </div>
    );
  };

  const getTitle = () => {
    if (suggestion.type === 'taskFolder') return 'Create Task Folder';
    if (suggestion.type === 'tasks') return 'Create Tasks';
    if (suggestion.type === 'habits') return 'Create Habits';
    if (suggestion.type === 'routine') return 'Create Routine';
    if (suggestion.type === 'project') return 'Create Project';
    if (suggestion.type === 'ability') return 'Create Ability';
    if (suggestion.type === 'quest') return 'Create Quest';
    if (suggestion.type === 'template') return 'Create Template';
    if (suggestion.type === 'program') return 'Create Program';
    return 'Apply Suggestion';
  };

  const getItemCount = () => {
    // ⚠️ The folder's tasks are what lands (and what the charge counts).
    if (suggestion.type === 'taskFolder') return folderTasks(suggestion).length;
    if (suggestion.items) return suggestion.items.length;
    if (suggestion.type === 'routine' && suggestion.habits) return suggestion.habits.length;
    return 1;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{getTitle()}</h3>
            <p className="text-sm text-muted-foreground">
              {getItemCount()} item{getItemCount() !== 1 ? 's' : ''} to create
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {renderContent()}
        </div>

        {/* ⚠️ Says NOTHING WAS CREATED, because the charge runs before the writes
            and a user who has just been refused needs to know their app is
            unchanged — not to go hunting for half-applied items. */}
        {chargeError && (
          <p className="px-4 pt-3 text-sm text-destructive">{chargeError}</p>
        )}
        <div className="flex gap-3 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted
              text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={isApplying}
            className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground
              hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {isApplying ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Apply
                {/* ⚠️⚠️ THE PRICE IS ON THE CONTROL, NOT IN A FOOTNOTE. Nobody is
                    charged for something they did not see the price of — and the
                    TOTAL is shown, not the unit, because the unit is not what
                    leaves the wallet when a suggestion holds five tasks.
                    ⚠️ Renders only while a price is ACTIVE (`priceFor` reads the
                    active list), so this is invisible until the owner switches
                    charging on. */}
                {totalCp > 0 && (
                  <span className="inline-flex items-center gap-1 rounded bg-black/15 px-1.5 py-0.5 text-xs">
                    <CoinAmount cp={totalCp} compact />
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
